import { API_BASE_URL } from '../config';
import {
  Campaign,
  CampaignDetail,
  CampaignCreateRequest,
  CampaignUpdateRequest,
  CampaignAddContactsRequest,
  CampaignContact
} from '../types';

// ==================== CAMPAIGNS ====================

export async function fetchCampaigns(accessToken: string): Promise<Campaign[]> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar campanhas');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function fetchCampaign(
  campaignId: number,
  accessToken: string
): Promise<CampaignDetail> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function createCampaign(
  data: CampaignCreateRequest,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao criar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function updateCampaign(
  campaignId: number,
  data: CampaignUpdateRequest,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao atualizar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function startCampaign(
  campaignId: number,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/start`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao iniciar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function pauseCampaign(
  campaignId: number,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/pause`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao pausar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function resumeCampaign(
  campaignId: number,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/resume`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao retomar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function completeCampaign(
  campaignId: number,
  accessToken: string
): Promise<Campaign> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/complete`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao completar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export interface CampaignRestartResult {
  campaign: Campaign;
  contactsReset: number;
}

export interface CampaignRestartRequest {
  startDate: string;
  endDate: string;
  runTime?: string;
  templateMode: 'SAME_FOR_ALL' | 'DIFFERENT_PER_DAY';
  defaultTemplateId?: string;
  defaultTemplateName?: string;
  defaultLanguageCode?: string;
  defaultVariableMapping?: string[];
  dayTemplates?: Array<{
    dayNumber: number;
    date?: string;
    templateId: string;
    templateName: string;
    languageCode?: string;
    variableMapping?: string; // JSON-serialized array, e.g., '["Nome", "Empresa"]'
  }>;
}

export async function restartCampaign(
  campaignId: number,
  data: CampaignRestartRequest,
  accessToken: string
): Promise<CampaignRestartResult> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/restart`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao reiniciar campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export interface CampaignRunResult {
  campaignId: number;
  sent: number;
  failed: number;
  error: string | null;
}

export async function runCampaignById(
  campaignId: number,
  accessToken: string,
  forceResend?: boolean
): Promise<CampaignRunResult> {
  const url = forceResend
    ? `${API_BASE_URL}/hsm/campaigns/${campaignId}/run?forceResend=true`
    : `${API_BASE_URL}/hsm/campaigns/${campaignId}/run`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao executar campanha');
  }

  const payload = await response.json();
  return payload.data || payload;
}

export async function runAllCampaigns(
  accessToken: string
): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/run`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao executar campanhas');
  }

  const payload = await response.json();
  return payload.data || payload;
}

export async function addCampaignContacts(
  campaignId: number,
  data: CampaignAddContactsRequest,
  accessToken: string
): Promise<{ added: number }> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/contacts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao adicionar contatos a campanha');
  }

  const payload = await response.json();
  return payload.data;
}

export async function fetchCampaignContacts(
  campaignId: number,
  accessToken: string
): Promise<CampaignContact[]> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}/contacts`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar contatos da campanha');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function deleteCampaign(
  campaignId: number,
  accessToken: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/hsm/campaigns/${campaignId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao excluir campanha');
  }
}
